import React from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowUpRight, ArrowRight, CheckCircle2, ShieldCheck, Sparkles, Target, TrendingUp, Award, Quote } from "lucide-react";
import Counter from "../components/Counter";
import { STATS, INDUSTRIES, INDUSTRY_HUES, SERVICES, COMPANY, TESTIMONIALS } from "../data/content";

const Home = () => {
  const heroService = SERVICES.slice(0, 4);

  return (
    <div data-testid="home-page" className="overflow-hidden">
      {/* HERO */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-28 spotlight">
        <div className="absolute inset-0 corporate-grid pointer-events-none opacity-60" />
        <div className="absolute inset-0 grain pointer-events-none opacity-30" />
        <div className="absolute top-20 -right-20 w-[32rem] h-[32rem] rounded-full bg-brand-green/10 blur-3xl float-slow pointer-events-none" />
        <div className="absolute top-1/2 -left-20 w-80 h-80 rounded-full bg-brand-gold/15 blur-3xl float-medium pointer-events-none" />
        {/* Decorative 3D shapes */}
        <div className="absolute top-32 right-1/3 w-20 h-20 rounded-2xl bg-gradient-to-br from-brand-green to-brand-green-light opacity-20 rotate-12 float-fast pointer-events-none hidden lg:block" />
        <div className="absolute bottom-20 left-1/4 w-16 h-16 rounded-full border-2 border-brand-gold/30 float-slow pointer-events-none hidden lg:block" />
        <div className="absolute top-1/2 right-1/4 w-12 h-12 bg-brand-gold/20 rotate-45 float-medium pointer-events-none hidden lg:block" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-green-50 border border-brand-green/15 text-xs font-bold tracking-[0.2em] uppercase text-brand-green mb-8">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-green animate-pulse" />
              Management Consulting · Est. Visakhapatnam
            </div>
            <h1 className="font-serif text-[2.75rem] sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-brand-ink">
              Strategy made
              <span className="text-brand-green font-serif italic"> measurable.</span>
              <br />
              Growth made <span className="text-brand-gold font-serif italic">inevitable.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-brand-slate max-w-xl leading-relaxed">
              {COMPANY.tagline} We help organizations optimize operations, enhance performance, and achieve sustainable growth with strategy that ships.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/services" data-testid="hero-learn-more" className="inline-flex items-center gap-2 bg-brand-green text-white px-7 py-4 rounded-sm font-medium hover:bg-brand-green-light transition-all hover:-translate-y-0.5 shadow-lg shadow-brand-green/20">
                Learn More <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/contact" data-testid="hero-contact" className="inline-flex items-center gap-2 border-2 border-brand-ink text-brand-ink px-7 py-4 rounded-sm font-medium hover:bg-brand-ink hover:text-white transition-colors">
                Contact Us <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-brand-slate">
              <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-brand-green" /> 21 industries served</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-brand-green" /> Boardroom-grade insights</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-brand-green" /> Outcome-led engagements</div>
            </div>
          </div>

          {/* Right visual */}
          <div className="lg:col-span-5 relative fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative aspect-[4/5] rounded-sm overflow-hidden shadow-2xl shadow-brand-green/20">
              <img src="https://images.unsplash.com/photo-1758691736493-aa6d22c0f8a6?w=1200&q=80" alt="Modern consulting boardroom" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/60 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className="text-xs tracking-[0.2em] uppercase text-brand-gold mb-2">Featured Engagement</div>
                <div className="font-serif text-2xl leading-tight">Transforming an enterprise into a digital-first operating model.</div>
              </div>
            </div>
            {/* Floating stat card */}
            <div className="absolute -bottom-8 -left-8 hidden md:block bg-white rounded-sm p-6 shadow-2xl shadow-brand-green/20 border border-black/5 max-w-[240px]">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs tracking-widest uppercase text-brand-slate">Avg. Impact</div>
                  <div className="font-serif text-4xl text-brand-green mt-2"><Counter to={3.4} suffix="x" /></div>
                  <div className="text-xs text-brand-slate mt-1">EBITDA improvement</div>
                </div>
                <TrendingUp className="h-5 w-5 text-brand-green" />
              </div>
            </div>
            <div className="absolute -top-6 -right-6 hidden md:block bg-brand-ink text-white rounded-full h-28 w-28 flex flex-col items-center justify-center text-center shadow-2xl float-medium">
              <div className="text-[10px] tracking-[0.2em] uppercase text-brand-gold">Since</div>
              <div className="font-serif text-2xl mt-0.5">2014</div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-black/5 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <div key={i} data-testid={`stat-${i}`} className="text-center md:text-left">
              <div className="font-serif text-5xl md:text-6xl text-brand-green tracking-tight">
                <Counter to={s.value} suffix={s.suffix} />
              </div>
              <div className="mt-3 text-sm text-brand-slate tracking-wide">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* INDUSTRIES WE COVER */}
      <section data-testid="industries-section" className="py-24 md:py-32 bg-brand-green-50/50">
        <div className="max-w-7xl mx-auto px-6 md:px-10 mb-14">
          <div className="text-xs font-bold tracking-[0.25em] uppercase text-brand-gold mb-4">21 Sectors. One Standard.</div>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight text-brand-ink max-w-3xl leading-[1.05]">
              Industries <em className="text-brand-green not-italic font-serif italic">we cover</em>.
            </h2>
            <p className="text-brand-slate max-w-md">Deep functional knowledge, anchored in real industry context — from banking to biotech, telecom to transportation.</p>
          </div>
        </div>

        {/* Marquee row 1 - Illustrated cards */}
        <div className="relative">
          <div className="flex marquee w-max gap-5 px-6">
            {[...INDUSTRIES, ...INDUSTRIES].map((ind, i) => {
              const hue = INDUSTRY_HUES[ind.hue];
              const IconCmp = Icons[ind.icon] || Icons.Briefcase;
              return (
                <div
                  key={i}
                  data-testid={`industry-${i}`}
                  className="relative w-64 h-44 rounded-sm overflow-hidden group flex-shrink-0 shadow-glass hover:shadow-card-hover hover:-translate-y-1 transition-all"
                  style={{ background: hue.bg }}
                >
                  {/* Decorative geometric accents */}
                  <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full" style={{ background: hue.accent, opacity: 0.12 }} />
                  <div className="absolute -bottom-10 -left-10 w-28 h-28 rounded-full border-2" style={{ borderColor: hue.accent, opacity: 0.15 }} />
                  <div className="absolute top-3 right-3 text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: hue.accent }}>0{(i % 8) + 1}</div>

                  {/* Icon */}
                  <div className="relative h-full flex flex-col justify-between p-5">
                    <div className="h-14 w-14 rounded-sm flex items-center justify-center backdrop-blur-sm" style={{ background: "rgba(255,255,255,0.12)", border: `1px solid ${hue.accent}33` }}>
                      <IconCmp className="h-7 w-7" style={{ color: hue.accent }} strokeWidth={1.5} />
                    </div>
                    <div>
                      <div className="font-serif text-lg leading-tight" style={{ color: hue.fg }}>{ind.name}</div>
                      <div className="mt-1.5 h-0.5 w-8 group-hover:w-16 transition-all duration-500" style={{ background: hue.accent }} />
                    </div>
                  </div>
                  <ArrowUpRight className="absolute bottom-4 right-4 h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: hue.accent }} />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES SNAPSHOT */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-14">
            <div className="md:col-span-7">
              <div className="text-xs font-bold tracking-[0.25em] uppercase text-brand-gold mb-4">What we do</div>
              <h2 className="font-serif text-4xl md:text-6xl tracking-tight text-brand-ink leading-[1.05]">Boardroom strategy. <span className="text-brand-green italic">Built to execute.</span></h2>
            </div>
            <div className="md:col-span-5 md:pt-12">
              <p className="text-brand-slate text-lg leading-relaxed">Eleven core capabilities — from process optimization through master data management — sequenced into outcome-led programs.</p>
              <Link to="/services" data-testid="home-all-services" className="inline-flex items-center gap-2 mt-6 text-brand-green font-medium hover:gap-3 transition-all">
                See all services <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {heroService.map((s, i) => (
              <Link key={s.slug} to="/services" data-testid={`home-service-${s.slug}`} className="group relative overflow-hidden rounded-sm border border-black/5 bg-white hover:shadow-card-hover transition-all">
                <div className="grid grid-cols-12 gap-0">
                  <div className="col-span-5 relative aspect-square">
                    <img src={s.img} alt={s.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="col-span-7 p-7 flex flex-col">
                    <div className="text-xs tracking-[0.2em] uppercase text-brand-slate">0{i + 1}</div>
                    <h3 className="font-serif text-2xl text-brand-ink mt-3 leading-tight">{s.title}</h3>
                    <p className="text-sm text-brand-slate mt-3 leading-relaxed line-clamp-3">{s.desc}</p>
                    <div className="mt-auto pt-5 flex items-center gap-2 text-sm font-medium text-brand-green group-hover:gap-3 transition-all">
                      Explore <ArrowUpRight className="h-4 w-4" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ENTERPRISE HEALTH ASSESSMENT BANNER */}
      <section data-testid="assessment-banner" className="relative py-24 md:py-32 bg-brand-ink text-white overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[40rem] h-[40rem] rounded-full bg-brand-green/30 blur-3xl float-slow" />
        <div className="absolute -bottom-32 -left-32 w-[32rem] h-[32rem] rounded-full bg-brand-gold/15 blur-3xl float-medium" />
        <div className="absolute inset-0 grain opacity-40 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="text-xs font-bold tracking-[0.25em] uppercase text-brand-gold mb-6">Enterprise Health Assessment</div>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight">
              Know where your organization <em className="text-brand-gold font-serif italic">stands</em>.
            </h2>
            <p className="mt-8 text-white/70 text-lg max-w-xl leading-relaxed">
              Every organization has hidden strengths, operational risks, financial inefficiencies, and growth opportunities. Understand your maturity across 10 critical business functions — before making strategic decisions.
            </p>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-6 max-w-2xl">
              <div className="flex items-start gap-3">
                <Target className="h-5 w-5 text-brand-gold flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-sm">8-12 Minutes</div>
                  <div className="text-xs text-white/50 mt-1">Assessment duration</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Sparkles className="h-5 w-5 text-brand-gold flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-sm">Instant Summary</div>
                  <div className="text-xs text-white/50 mt-1">Executive insights</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <ShieldCheck className="h-5 w-5 text-brand-gold flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-sm">Confidential</div>
                  <div className="text-xs text-white/50 mt-1">100% secure</div>
                </div>
              </div>
            </div>
            <Link to="/assessment" data-testid="home-start-assessment" className="inline-flex items-center gap-3 mt-12 bg-brand-gold text-brand-ink px-8 py-4 rounded-sm font-semibold hover:bg-white transition-colors">
              Start Enterprise Assessment <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-4">
              {[
                { t: "Strategic", n: "01" }, { t: "Financial", n: "02" }, { t: "Operational", n: "03" }, { t: "Risk", n: "04" },
                { t: "Governance", n: "05" }, { t: "Digital", n: "06" }, { t: "Human Capital", n: "07" }, { t: "ESG", n: "08" },
              ].map((m, i) => (
                <div key={i} className="bg-white/5 border border-white/10 backdrop-blur-sm rounded-sm p-5 hover:bg-white/10 transition-colors">
                  <div className="text-xs tracking-widest text-brand-gold">{m.n}</div>
                  <div className="font-serif text-xl mt-2">{m.t}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS - floating sliding cards */}
      <section className="py-24 md:py-32 bg-white relative overflow-hidden">
        <div className="absolute inset-0 corporate-grid pointer-events-none opacity-50" />
        <div className="absolute -top-32 left-1/3 w-96 h-96 rounded-full bg-brand-green/8 blur-3xl float-slow pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-brand-gold/8 blur-3xl float-medium pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 mb-16 text-center">
          <Award className="h-10 w-10 text-brand-gold mx-auto" />
          <div className="text-xs font-bold tracking-[0.25em] uppercase text-brand-gold mt-6 mb-3">What leaders say</div>
          <h2 className="font-serif text-4xl md:text-5xl text-brand-ink tracking-tight">Boardroom-grade <em className="text-brand-green italic">conviction.</em></h2>
        </div>

        {/* Featured testimonial */}
        <div className="relative max-w-5xl mx-auto px-6 md:px-10">
          <div className="relative bg-gradient-to-br from-brand-green to-brand-green-light text-white rounded-sm p-10 md:p-16 overflow-hidden shadow-card-hover">
            <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-brand-gold/20 blur-2xl" />
            <div className="absolute -bottom-12 -left-12 w-56 h-56 rounded-full bg-white/10 blur-2xl" />
            <Quote className="h-14 w-14 text-brand-gold/40 mb-6" />
            <blockquote data-testid="testimonial-featured" className="relative font-serif italic text-2xl md:text-4xl leading-tight max-w-4xl">
              "{TESTIMONIALS[0].quote}"
            </blockquote>
            <div className="relative mt-10 inline-flex items-center gap-4">
              <div className="relative h-14 w-14 rounded-full overflow-hidden ring-2 ring-brand-gold/60 ring-offset-2 ring-offset-brand-green">
                <img data-testid="testimonial-featured-avatar" src={TESTIMONIALS[0].avatar} alt={TESTIMONIALS[0].name} className="absolute inset-0 w-full h-full object-cover" />
              </div>
              <div className="text-left">
                <div data-testid="testimonial-name" className="font-medium text-lg">{TESTIMONIALS[0].name}</div>
                <div className="text-sm text-white/70">{TESTIMONIALS[0].role}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Floating sliding testimonial strip */}
        <div className="relative mt-16">
          <div className="flex marquee-slow w-max gap-6 px-6">
            {[...TESTIMONIALS.slice(1), ...TESTIMONIALS.slice(1), ...TESTIMONIALS.slice(1)].map((t, i) => (
              <div
                key={i}
                data-testid={`testimonial-slide-${i}`}
                className="relative w-[420px] flex-shrink-0 bg-white border border-black/5 rounded-sm p-7 shadow-glass hover:shadow-card-hover hover:-translate-y-1 transition-all"
              >
                <Quote className="h-5 w-5 text-brand-gold mb-3" />
                <p className="text-brand-ink font-serif italic text-lg leading-snug">"{t.quote}"</p>
                <div className="mt-5 pt-5 border-t border-black/5 flex items-center gap-3">
                  <div className="relative h-10 w-10 rounded-full overflow-hidden ring-1 ring-brand-green/20">
                    <img src={t.avatar} alt={t.name} className="absolute inset-0 w-full h-full object-cover" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-brand-ink">{t.name}</div>
                    <div className="text-xs text-brand-slate">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
